import {
  capsuleAabb,
  sweptCircleCapsuleIntersects,
  sweptCirclesIntersect,
} from "./collision";
import {
  circleCapsuleContact,
  circleContact,
  circularBoundaryContact,
  type Contact,
} from "./collision-response";
import type { CompetitiveConfig } from "./config";
import { fromAngle, scale, type Vec2 } from "./math";
import { SpatialHash } from "./spatial-hash";
import type { BodySegment, DeathReason, SnakeState } from "./types";

export interface PendingDeath {
  readonly reason: DeathReason;
  readonly sourceSnakeId?: string;
}

interface CollisionInput {
  readonly alive: readonly SnakeState[];
  readonly previousPositions: ReadonlyMap<string, Vec2>;
  readonly atSeconds: number;
  readonly config: CompetitiveConfig;
  readonly assistEnabled: boolean;
  readonly isProtected: (snakeId: string, atSeconds: number) => boolean;
  readonly headRadius: (snake: SnakeState) => number;
  readonly bounce: (
    snake: SnakeState,
    contact: Contact,
    contactKey: string,
    atSeconds: number,
    share?: number,
  ) => void;
  readonly contactCooldownSeconds: number;
  readonly setHeadingAfterBoundaryBounce: (
    snake: SnakeState,
    contact: Contact,
    contactKey: string,
    atSeconds: number,
  ) => void;
  readonly contactCooldownUntil: Map<string, number>;
  readonly buildBodySegments: (
    snakes: readonly SnakeState[],
    excludeNewestHeadMotion?: boolean,
  ) => BodySegment[];
}

export interface CollisionResult {
  readonly pendingDeaths: Map<string, PendingDeath>;
  readonly bodySegments: BodySegment[];
  readonly spatialBucketCount: number;
}

export const detectMatchCollisions = (
  input: CollisionInput,
): CollisionResult => {
  const pendingDeaths = new Map<string, PendingDeath>();
  const mark = (snakeId: string, death: PendingDeath): void => {
    if (!pendingDeaths.has(snakeId)) pendingDeaths.set(snakeId, death);
  };

  for (let firstIndex = 0; firstIndex < input.alive.length; firstIndex += 1) {
    const first = input.alive[firstIndex];
    if (!first) continue;
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < input.alive.length;
      secondIndex += 1
    ) {
      const second = input.alive[secondIndex];
      if (!second) continue;
      if (
        sweptCirclesIntersect(
          input.previousPositions.get(first.id) ?? first.position,
          first.position,
          input.headRadius(first),
          input.previousPositions.get(second.id) ?? second.position,
          second.position,
          input.headRadius(second),
        )
      ) {
        const neutralProtection =
          input.isProtected(first.id, input.atSeconds) ||
          input.isProtected(second.id, input.atSeconds);
        const assistedContact =
          input.assistEnabled &&
          (first.id === "player" || second.id === "player");
        if (neutralProtection || assistedContact) {
          const contact = circleContact(
            first.position,
            input.headRadius(first),
            second.position,
            input.headRadius(second),
            fromAngle(first.heading + Math.PI),
          ) ?? {
            normal: fromAngle(first.heading + Math.PI),
            penetration: 0,
            point: { ...first.position },
          };
          const pairKey = `head:${first.id}:${second.id}`;
          input.bounce(
            first,
            contact,
            `${pairKey}:${first.id}`,
            input.atSeconds,
            0.5,
          );
          input.bounce(
            second,
            { ...contact, normal: scale(contact.normal, -1) },
            `${pairKey}:${second.id}`,
            input.atSeconds,
            0.5,
          );
          input.contactCooldownUntil.set(
            `head-pair:${[first.id, second.id].sort().join(":")}`,
            input.atSeconds + input.contactCooldownSeconds,
          );
        } else {
          mark(first.id, {
            reason: "head-to-head",
            sourceSnakeId: second.id,
          });
          mark(second.id, {
            reason: "head-to-head",
            sourceSnakeId: first.id,
          });
        }
      }
    }
  }

  for (const snake of input.alive) {
    const contact = circularBoundaryContact(
      snake.position,
      input.headRadius(snake),
      input.config.arenaRadius,
    );
    if (!contact) continue;
    if (input.assistEnabled && snake.id === "player") {
      input.setHeadingAfterBoundaryBounce(
        snake,
        contact,
        `boundary:${snake.id}`,
        input.atSeconds,
      );
    } else {
      mark(snake.id, { reason: "boundary" });
    }
  }

  const spatial = new SpatialHash<BodySegment>(input.config.spatialCellSize);
  const bodySegments = input.buildBodySegments(input.alive, true);
  for (const segment of bodySegments) {
    spatial.insert(
      segment,
      capsuleAabb(segment.start, segment.end, segment.radius),
    );
  }

  for (const snake of input.alive) {
    const radius = input.headRadius(snake);
    const previousPosition =
      input.previousPositions.get(snake.id) ?? snake.position;
    const candidates = spatial
      .query(capsuleAabb(previousPosition, snake.position, radius))
      .sort(
        (left, right) =>
          left.snakeId.localeCompare(right.snakeId) ||
          left.segmentIndex - right.segmentIndex,
      );
    const collision = candidates.find(
      (segment) =>
        segment.snakeId !== snake.id &&
        sweptCircleCapsuleIntersects(
          previousPosition,
          snake.position,
          radius,
          segment.start,
          segment.end,
          segment.radius,
        ),
    );
    if (!collision) continue;
    const neutralProtection =
      input.isProtected(snake.id, input.atSeconds) ||
      input.isProtected(collision.snakeId, input.atSeconds);
    const assistedBounce =
      input.assistEnabled &&
      snake.id === "player" &&
      collision.snakeId !== "player";
    const recentAssistedHeadContact =
      input.assistEnabled &&
      (input.contactCooldownUntil.get(
        `head-pair:${[snake.id, collision.snakeId].sort().join(":")}`,
      ) ?? 0) > input.atSeconds;
    if (neutralProtection || assistedBounce || recentAssistedHeadContact) {
      const contact = circleCapsuleContact(
        snake.position,
        radius,
        collision.start,
        collision.end,
        collision.radius,
        fromAngle(snake.heading + Math.PI),
      ) ?? {
        normal: fromAngle(snake.heading + Math.PI),
        penetration: 0,
        point: { ...snake.position },
      };
      input.bounce(
        snake,
        contact,
        `body:${snake.id}:${collision.snakeId}:${collision.segmentIndex}`,
        input.atSeconds,
      );
    } else {
      mark(snake.id, {
        reason: "body",
        sourceSnakeId: collision.snakeId,
      });
    }
  }
  return {
    pendingDeaths,
    bodySegments,
    spatialBucketCount: spatial.bucketCount,
  };
};
